// Image sequence canvas scroll-driven animation
// Uses vanilla JS with modern syntax, loaded once per page.

(function () {
  // Avoid double-initialization if script is included more than once
  /** @type {any} */
  const globalAny = window;
  if (globalAny.__imageSequenceCanvasInitialized) return;
  globalAny.__imageSequenceCanvasInitialized = true;

  /**
   * Clamp value between 0 and 1
   * @param {number} value
   * @returns {number}
   */
  /**
   * @param {number} value
   * @returns {number}
   */
  const clamp01 = (value) => {
    if (value < 0) return 0;
    if (value > 1) return 1;
    return value;
  };

  /**
   * Pad integer with leading zeroes
   * @param {number} num
   * @param {number} size
   * @returns {string}
   */
  const padNumber = (num, size) => {
    let s = String(num);
    while (s.length < size) s = `0${s}`;
    return s;
  };

  /**
   * Initialize one image-sequence root element
   * @param {HTMLElement} root
   */
  /**
   * @param {HTMLElement} root
   */
  const initSequence = (root) => {
    /** @type {HTMLCanvasElement|null} */
    const canvas = root.querySelector('[data-sequence-canvas]');
    /** @type {HTMLImageElement|null} */
    const poster = /** @type {HTMLImageElement|null} */ (root.querySelector('.image-sequence-canvas__poster'));
    /** @type {HTMLElement[]} */
    const bullets = Array.from(root.querySelectorAll('[data-sequence-bullets] li'));

    if (!canvas || !canvas.getContext || !root.dataset.baseUrl) return;

    const prefersReduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;

    const cfg = {
      baseUrl: root.dataset.baseUrl,
      mobileBase: root.dataset.mobileBase,
      ext: root.dataset.ext || 'webp',
      mobileExt: root.dataset.mobileExt || root.dataset.ext || 'webp',
      // Hard-coded sequence specifics for this section
      frames: 241, // 00000–00240 inclusive
      pad: 5,
      duration: 9.6, // seconds
      // Scroll distance (in viewport heights) is configurable per section
      scrollLength: Number(root.dataset.scrollLength) || 240,
      eager: Number(root.dataset.eager) || 8,
      preloadWindow: Number(root.dataset.preloadWindow) || 5,
      width: Number(root.dataset.width) || 1080,
      height: Number(root.dataset.height) || 1080,
      mobileWidth: Number(root.dataset.mobileWidth) || 900,
      mobileHeight: Number(root.dataset.mobileHeight) || 900,
      shiftList: false,
      honorReduceMotion: root.dataset.honorReduceMotion === 'true',
    };

    // TOGGLE OPTIMIZATIONS: Set to false to test before optimization (loads all frames immediately)
    const ENABLE_OPTIMIZATIONS = true; 

    /**
     * Get current viewport state and determine if mobile should be used
     * @returns {{isMobile: boolean, baseUrl: string, ext: string, width: number, height: number}}
     */
    const getViewportConfig = () => {
      const isMobileViewport = window.matchMedia?.('(max-width: 799px)').matches;
      const useMobile = Boolean(isMobileViewport && (cfg.mobileBase || cfg.mobileExt !== cfg.ext));
      const baseUrl = useMobile && cfg.mobileBase ? cfg.mobileBase : cfg.baseUrl;
      const ext = useMobile ? cfg.mobileExt : cfg.ext;
      const width = useMobile ? cfg.mobileWidth : cfg.width;
      const height = useMobile ? cfg.mobileHeight : cfg.height;
      return { isMobile: useMobile, baseUrl, ext, width, height };
    };

    // Initialize with current viewport config
    let currentConfig = getViewportConfig();
    let previousIsMobile = currentConfig.isMobile;

    const targetWidth = currentConfig.width;
    const targetHeight = currentConfig.height;

    canvas.width = targetWidth;
    canvas.height = targetHeight;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const frames = new Array(cfg.frames);
    let latestFrame = -1;
    let start = 0;
    let end = 0;
    let hasCompletedAnimation = false; // Track if animation has been completed

    // Initialize variables (needed for both modes)
    /** @type {number[]} */
    let loadingQueue = [];
    let activeLoads = 0;
    let lastPreloadedFrame = -1;

    /**
     * Clear all loaded frames when switching between mobile/desktop
     */
    const clearFrames = () => {
      for (let i = 0; i < frames.length; i += 1) {
        frames[i] = undefined;
      }
      latestFrame = -1;
      loadingQueue = [];
      activeLoads = 0;
      lastPreloadedFrame = -1;
    };

    /**
     * Check if viewport changed and update config if needed
     * @returns {boolean} true if viewport changed
     */
    const checkViewportChange = () => {
      const newConfig = getViewportConfig();
      if (newConfig.isMobile !== previousIsMobile) {
        // Viewport changed - clear frames and update config
        clearFrames();
        currentConfig = newConfig;
        previousIsMobile = Boolean(newConfig.isMobile);
        
        // Update canvas dimensions
        canvas.width = newConfig.width;
        canvas.height = newConfig.height;
        
        return true;
      }
      // Update config even if mobile state didn't change (in case baseUrl/ext changed)
      currentConfig = newConfig;
      return false;
    };

    /**
     * Build URL for a given frame index.
     * NOTE: Index 0 maps to ..._00000, so files should be
     * Animation_normal_00000.jpg through Animation_normal_00240.jpg
     * when frame_count is 241.
     *
     * @param {number} i
     * @returns {string}
     */
    const frameSrc = (i) => {
      // Always use current config to ensure correct baseUrl/ext
      const config = getViewportConfig();
      return `${config.baseUrl}${padNumber(i, cfg.pad)}.${config.ext}`;
    };

    /**
     * @param {number} index
     * @returns {Promise<HTMLImageElement>}
     */
    const loadFrame = (index) => {
      // Check if viewport changed before loading
      checkViewportChange();
      
      // If frame is already loaded, return it (but verify it's from current config)
      if (frames[index]) {
        // Verify the loaded frame matches current config by checking if it's a promise
        // If it's already resolved, we trust it (could add URL validation here if needed)
        return frames[index];
      }
      
      frames[index] = new Promise((resolve, reject) => {
        const img = new Image();
        // Add decoding hint for better performance
        img.decoding = 'async';
        // Don't use lazy loading for canvas images - they need to load immediately when requested
        // img.loading = 'lazy'; // Removed - causes issues with canvas rendering
        
        // Get current config to ensure we use correct URL
        const config = getViewportConfig();
        const src = `${config.baseUrl}${padNumber(index, cfg.pad)}.${config.ext}`;
        img.src = src;
        
        img.onload = () => {
          // Double-check viewport hasn't changed during load
          const currentConfig = getViewportConfig();
          if (currentConfig.isMobile !== previousIsMobile) {
            // Viewport changed during load - reject and let it reload with new config
            reject(new Error('Viewport changed during load'));
            return;
          }
          resolve(img);
        };
        img.onerror = reject;
      });
      return frames[index];
    };

    /**
     * @param {number} index
     */
    const drawFrame = (index) => {
      if (index === latestFrame) return;
      
      // Ensure current frame is loaded immediately (bypass queue for current frame)
      if (ENABLE_OPTIMIZATIONS) {
        // Remove from queue if it's there to prioritize immediate loading
        const queueIndex = loadingQueue.indexOf(index);
        if (queueIndex > -1) {
          loadingQueue.splice(queueIndex, 1);
        }
      }
      
      loadFrame(index)
        .then((img) => {
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          latestFrame = index;
          if (poster) poster.classList.add('is-hidden');
        })
        .catch(() => {});
    };

    // Performance optimization: Only load frames on-demand (when ENABLE_OPTIMIZATIONS is true)
    let preloadFrames;
    
    if (ENABLE_OPTIMIZATIONS) {
      // OPTIMIZED MODE: Load frames on-demand with preload window
      const PRELOAD_WINDOW = cfg.preloadWindow; // Load frames ahead and behind current frame
      const MAX_CONCURRENT_LOADS = 3; // Limit concurrent image loads

      /**
       * Preload frames in a window around the current frame
       * @param {number} currentFrame
       */
      preloadFrames = (currentFrame) => {
        // Only preload if frame changed significantly
        if (Math.abs(currentFrame - lastPreloadedFrame) < 2) return;
        lastPreloadedFrame = currentFrame;

        const framesToLoad = [];
        const start = Math.max(0, currentFrame - PRELOAD_WINDOW);
        const end = Math.min(cfg.frames - 1, currentFrame + PRELOAD_WINDOW);

        for (let i = start; i <= end; i += 1) {
          if (!frames[i] && !loadingQueue.includes(i)) {
            framesToLoad.push(i);
          }
        }

        // Add to queue, prioritizing frames closer to current
        framesToLoad.sort((a, b) => Math.abs(a - currentFrame) - Math.abs(b - currentFrame));
        loadingQueue.push(...framesToLoad);
        processLoadQueue();
      };

      /**
       * Process the loading queue with concurrency limit
       */
      const processLoadQueue = () => {
        while (activeLoads < MAX_CONCURRENT_LOADS && loadingQueue.length > 0) {
          const index = loadingQueue.shift();
          if (typeof index !== 'number') break;
          // Only load if not already loaded or loading
          if (!frames[index]) {
            activeLoads += 1;
            loadFrame(index)
              .then(() => {
                activeLoads -= 1;
                processLoadQueue();
              })
              .catch(() => {
                activeLoads -= 1;
                processLoadQueue();
              });
          }
        }
      };

      // Load initial eager frames (first few frames) immediately
      const eagerCount = Math.min(cfg.eager, cfg.frames);
      for (let i = 0; i < eagerCount; i += 1) {
        // Load first frame immediately, queue the rest
        if (i === 0) {
          // Load and draw first frame immediately for instant display
          loadFrame(0).then(() => {
            // Draw first frame initially so it's visible before animation starts
            drawFrame(0);
          }).catch(() => {});
        } else {
          loadingQueue.push(i);
        }
      }
      processLoadQueue();
    } else {
      // OLD BEHAVIOR: Load all frames immediately (for comparison)
      preloadFrames = () => {}; // No-op function
      
      const eagerCount = Math.min(cfg.eager, cfg.frames);
      for (let i = 0; i < eagerCount; i += 1) {
        if (i === 0) {
          // Load and draw first frame immediately for instant display
          loadFrame(0).then(() => {
            // Draw first frame initially so it's visible before animation starts
            drawFrame(0);
          }).catch(() => {});
        } else {
          loadFrame(i);
        }
      }

      const idleLoad = () => {
        for (let i = eagerCount; i < cfg.frames; i += 1) loadFrame(i);
      };

      if (window.requestIdleCallback) {
        window.requestIdleCallback(idleLoad);
      } else {
        setTimeout(idleLoad, 0);
      }
    }

    let lastMeasureTime = 0;
    const MEASURE_THROTTLE = 100; // Throttle measurements to avoid excessive calculations

    const measure = () => {
      const now = performance.now();
      // Throttle measurements to avoid excessive recalculations
      if (now - lastMeasureTime < MEASURE_THROTTLE) return;
      lastMeasureTime = now;

      // IMPORTANT:
      // `root` is the sticky/pinned element (`.image-sequence-canvas__pin`).
      // When sticky, `root.getBoundingClientRect().top` becomes ~0 and is NOT a stable anchor for
      // mapping scroll progress (it makes `start` jump to `scrollY`).
      // Use the section container as the stable anchor: `scrollY + sectionRect.top` remains constant
      // while scrolling because sectionRect.top moves inversely with scrollY.
      const sectionEl = /** @type {HTMLElement|null} */ (root.closest('.image-sequence-canvas'));
      const rect = (sectionEl || root).getBoundingClientRect();
      const scrollY = window.scrollY || window.pageYOffset || 0;
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 1;
      const headerHeight = parseInt(getComputedStyle(document.body).getPropertyValue('--header-height')) || 60;
      const stickyHeight = Math.max(viewportHeight - headerHeight, 1); // height the sticky region can actually occupy

      // The section is cfg.scrollLength vh tall. The sticky child effectively has a height of
      // (stickyHeight / viewportHeight * 100) vh. Map the scroll so the animation runs during the
      // pinned portion that remains after accounting for the header.
      const stickyVh = (stickyHeight / viewportHeight) * 100;
      const extraVh = Math.max(cfg.scrollLength - stickyVh, 1); // prevent divide-by-zero

      const newStart = scrollY + rect.top;
      // Ensure progress reaches 1.0 (full animation) by mapping over the full scroll range
      const newEnd = newStart + stickyHeight * (extraVh / 100);

      // Only update if values have changed significantly (avoid unnecessary updates)
      if (Math.abs(newStart - start) > 1 || Math.abs(newEnd - end) > 1) {
        start = newStart;
        end = newEnd;
      }
    };

    measure();
    
    // Handle resize events - check for viewport changes and remeasure
    window.addEventListener('resize', () => {
      const viewportChanged = checkViewportChange();
      if (viewportChanged) {
        // Viewport changed - redraw current frame with new dimensions
        if (latestFrame >= 0) {
          // Clear and reload current frame with new config
          const currentFrame = latestFrame;
          latestFrame = -1; // Force redraw
          drawFrame(currentFrame);
        }
      }
      measure();
    });

    // Track if we've measured while section is in viewport
    let hasMeasuredInView = false;
    
    // Remeasure when page becomes visible again (handles tab switching, etc.)
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        // Only remeasure if we're not currently in the animation range
        const scrollY = window.scrollY || window.pageYOffset || 0;
        const isInRange = scrollY >= start && scrollY <= end;
        if (!isInRange) {
          // Delay slightly to allow layout to settle
          setTimeout(measure, 50);
          hasMeasuredInView = false;
        }
      }
    });

    // Use IntersectionObserver to remeasure when section first enters viewport
    // Only trigger once when section first becomes visible, not continuously
    let intersectionObserver;
    if (typeof IntersectionObserver !== 'undefined') {
      intersectionObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            // Only remeasure when section first enters viewport (not continuously)
            if (entry.isIntersecting && !hasMeasuredInView) {
              hasMeasuredInView = true;
              // Check if we're outside the animation range before remeasuring
              const scrollY = window.scrollY || window.pageYOffset || 0;
              const isInRange = scrollY >= start && scrollY <= end;
              if (!isInRange) {
                setTimeout(measure, 50);
              }
            } else if (!entry.isIntersecting) {
              // Reset flag when section leaves viewport
              hasMeasuredInView = false;
            }
          });
        },
        {
          rootMargin: '100px', // Smaller margin, only trigger when closer to viewport
          threshold: 0
        }
      );
      intersectionObserver.observe(root);
    }

    // Track last scroll position to detect significant scroll changes
    let lastScrollY = window.scrollY || window.pageYOffset || 0;
    let remeasureCheckCounter = 0;
    const REMEASURE_CHECK_INTERVAL = 120; // Check every 120 frames (~2 seconds at 60fps) - less frequent
    let isComingFromBelow = false; // Track if we're scrolling back from past the section

    /**
     * Return usable height inside the bullets container (excludes padding).
     * This is the real space the absolutely-positioned bullets must fit into on mobile.
     * @returns {number|null}
     */
    const getBulletsUsableHeight = () => {
      const bulletsContainer = /** @type {HTMLElement|null} */ (root.querySelector('[data-sequence-bullets]'));
      if (!bulletsContainer) return null;

      const rect = bulletsContainer.getBoundingClientRect();
      const styles = window.getComputedStyle(bulletsContainer);
      const paddingTop = parseFloat(styles.paddingTop) || 0;
      const paddingBottom = parseFloat(styles.paddingBottom) || 0;

      return Math.max(0, rect.height - paddingTop - paddingBottom);
    };

    /**
     * Calculate available space for content on mobile
     * @returns {{availableSpace: number, headerHeight: number, animationHeight: number, viewportHeight: number, viewportCategory: string}}
     */
    const calculateAvailableSpace = () => {
      const viewportHeight = window.innerHeight;
      const headerHeight = parseInt(getComputedStyle(document.body).getPropertyValue('--header-height')) || 60;
      const animationHeight = viewportHeight * 0.55; // 55vh

      // Prefer measuring the real bullets container height (more reliable than innerHeight math on mobile)
      const measuredBulletsSpace = getBulletsUsableHeight();
      const availableSpace = typeof measuredBulletsSpace === 'number'
        ? measuredBulletsSpace
        : viewportHeight - animationHeight - headerHeight;
      
      // Categorize viewport height
      let viewportCategory = 'large';
      if (viewportHeight < 700) {
        viewportCategory = 'small';
      } else if (viewportHeight < 850) {
        viewportCategory = 'medium';
      }
      
      return {
        availableSpace,
        headerHeight,
        animationHeight,
        viewportHeight,
        viewportCategory
      };
    };

    /**
     * Update viewport category classes on root element
     */
    const updateViewportClasses = () => {
      const space = calculateAvailableSpace();
      root.classList.remove('viewport-small', 'viewport-medium', 'viewport-large');
      root.classList.add(`viewport-${space.viewportCategory}`);
    };
    
    // Initialize viewport classes on mobile
    const isMobile = window.innerWidth <= 749;
    if (isMobile) {
      updateViewportClasses();
    }
    
    // Add resize listener for mobile responsive updates
    const handleMobileResize = () => {
      const isMobileNow = window.innerWidth <= 749;
      if (isMobileNow) {
        updateViewportClasses();
        // Trigger bullet update to recalculate with new viewport dimensions
        const scrollY = window.scrollY || window.pageYOffset || 0;
        const rect = root.getBoundingClientRect();
        const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 1;
        const headerHeight = parseInt(getComputedStyle(document.body).getPropertyValue('--header-height')) || 60;
        const isVisible = rect.bottom > headerHeight && rect.top < viewportHeight;
        const stickyAddToCartResize = document.querySelector('.sticky-add-to-cart');
        const isStickyAddToCartHiddenResize = !!(stickyAddToCartResize && stickyAddToCartResize.getAttribute('aria-hidden') === 'true');
        let raw = (scrollY - start) / (end - start);
        
        // Store original progress for first bullet (not delayed)
        const rawOriginal = raw;
        const progressOriginal = clamp01(rawOriginal);
        
        // Apply mobile delay - shift animation start later on mobile (for frameIndex only)
        const mobileDelay = 0.12; // 12% delay on mobile (callouts start later)
        if (raw > 0) {
          // Adjust raw progress to account for delay
          // When raw = mobileDelay, progress should be 0
          // When raw = 1, progress should be 1
          raw = Math.max(0, (raw - mobileDelay) / (1 - mobileDelay));
        }
        
        const progress = clamp01(raw);
        const frameIndex = Math.round(progress * (cfg.frames - 1));
        // Pass original progress so first bullet isn't affected by mobileDelay
        updateBullets(progressOriginal, frameIndex, isStickyAddToCartHiddenResize);
      } else {
        // No longer mobile, remove viewport classes
        root.classList.remove('viewport-small', 'viewport-medium', 'viewport-large');
      }
    };
    
    window.addEventListener('resize', handleMobileResize);
    window.addEventListener('orientationchange', () => {
      // Delay to allow viewport to update after orientation change
      setTimeout(handleMobileResize, 100);
    });

    /**
     * @param {number} progress
     * @param {number} frameIndex
     * @param {boolean} [isStickyAddToCartHidden] - Whether the sticky add-to-cart is hidden (closed)
     */
    const updateBullets = (progress, frameIndex, isStickyAddToCartHidden = false) => {
      // Use normal animation timeline (not remapped) - animation runs for full scroll range
      const t = progress * cfg.duration;
      
      // Check if mobile for delay calculations
      const isMobile = window.innerWidth <= 749;
      const mobileStickyDelay = isMobile ? 0.08 : 0; // 0.1 second delay on mobile for sticky CTA activation
      
      let active = -1;
      const lastBulletIndex = bullets.length - 1;
      
      // Calculate all bullet cue times first
      const bulletCues = bullets.map((el, idx) => {
        const element = /** @type {HTMLElement} */ (el);
        const frameAttr = element.getAttribute('data-frame');
        if (frameAttr !== null && frameAttr !== '' && !isNaN(Number(frameAttr))) {
          const frameNum = Number(frameAttr);
          return (frameNum / (cfg.frames - 1)) * cfg.duration;
        } else {
          return Number(element.dataset.cue || 0);
        }
      });
      
      // Get second bullet's cue time (if it exists)
      const secondBulletCue = bullets.length > 1 ? bulletCues[1] : Infinity;

      // Completion behavior:
      // - When progress is at the end, keep last bullet active.
      // - When reversing (progress decreases), allow callouts to change back.
      // We still keep the "scrolled past" case locked to last via `step()` calling updateBullets(1.0,...).
      if (progress >= 1.0) {
        active = lastBulletIndex;
      } else if (progress <= 0) {
        // Before animation starts: activate first bullet if sticky add-to-cart is closed
        // This happens right after sticky closes, before animation timeline starts
        // On mobile, add a small delay before activating first bullet
        if (isStickyAddToCartHidden && bullets.length > 0) {
          // On mobile, require minimum time delay before activating first bullet
          if (isMobile && t < mobileStickyDelay) {
            active = -1;
          } else {
            active = 0;
          }
        } else {
          active = -1;
        }
        hasCompletedAnimation = false;
      } else {
        // Normal animation range: use cue-based logic to find which bullet should be active
        const mobileDelay = isMobile ? 0.12 : 0; // 12% delay for bullets after first
        bullets.forEach((el, index) => {
          const cue = bulletCues[index];
          if (cue !== undefined) {
            if (index === 0) {
              // First bullet: use original timing (no mobileDelay, only mobileStickyDelay)
              if (t >= cue) {
                active = index;
              }
            } else {
              // Other bullets: apply mobileDelay
              const delayedT = isMobile ? Math.max(0, (t - (mobileDelay * cfg.duration)) / (1 - mobileDelay)) : t;
              if (delayedT >= cue) {
                active = index;
              }
            }
          }
        });
        
        // Special case: activate first bullet when sticky add-to-cart closes
        // This happens right when the section starts actively animating
        // The sticky add-to-cart hides itself when the section is actively animating
        // On mobile, add a small delay before activating first bullet (only mobileStickyDelay, not mobileDelay)
        if (isStickyAddToCartHidden && bullets.length > 0) {
          // If sticky is hidden (closed), activate first bullet
          // But only if animation time hasn't reached second bullet's cue yet
          // On mobile, also require minimum time delay (mobileStickyDelay only)
          if (secondBulletCue !== undefined && t < secondBulletCue) {
            if (isMobile && t < mobileStickyDelay) {
              active = -1;
            } else {
              active = 0;
            }
          }
        }
      }

      if (isMobile && bullets.length > 0) {
        // Mobile: Stacked effect - active on top, previous stacked behind, next preview
        // First bullet is not active by default (active = -1 until its cue time is reached)
        const effectiveActive = active >= 0 ? active : -1;
        const bulletsContainer = /** @type {HTMLElement|null} */ (root.querySelector('[data-sequence-bullets]'));
        
        // Calculate available space
        const space = calculateAvailableSpace();
        updateViewportClasses();
        
        // Check if all bullets have been shown (active is the last one and we're past its cue time)
        const lastBullet = bullets[lastBulletIndex];
        let lastBulletCue = 0;
        if (lastBullet) {
          const lastBulletEl = /** @type {HTMLElement} */ (lastBullet);
          const frameAttr = lastBulletEl.getAttribute('data-frame');
          if (frameAttr !== null && frameAttr !== '' && !isNaN(Number(frameAttr))) {
            // Calculate cue time from frame number
            const frameNum = Number(frameAttr);
            lastBulletCue = (frameNum / (cfg.frames - 1)) * cfg.duration;
          } else {
            // Use cue time directly
            lastBulletCue = Number(lastBulletEl.dataset.cue || 0);
          }
        }
        // If progress >= 1.0, animation is complete - keep last bullet active
        // Also trigger when progress is very high (0.95+) or when last bullet has been active for a while
        const allShown = progress >= 0.95 || (effectiveActive >= lastBulletIndex && t >= lastBulletCue + 0.5);
        // Only show past bullets when truly at the end (progress >= 1.0) to allow proper scrolling back
        const isAtEnd = progress >= 1.0;
        
        if (allShown && bulletsContainer) {
          // Keep last bullet visible - don't fade it out
          const lastBullet = bullets[lastBulletIndex];
          if (lastBullet) {
            lastBullet.classList.add('is-active');
            lastBullet.classList.remove('is-past', 'is-far-past', 'is-adjacent', 'is-preview');
          }
          
          // Only keep past bullets visible when truly at the end (not when scrolling back)
          if (isAtEnd) {
            // Keep past bullets visible to fill space at the end
            bullets.forEach((el, index) => {
              if (index < lastBulletIndex) {
                // Keep past bullets visible but faded
                el.classList.add('is-past');
                el.classList.remove('is-far-past', 'is-active', 'is-adjacent', 'is-preview');
                // Ensure they're visible
                el.style.opacity = '';
                el.style.pointerEvents = 'none';
              }
            });
          }
          
          // Mark as complete only when truly at the end
          if (isAtEnd) {
            const container = /** @type {HTMLElement} */ (bulletsContainer);
            container.classList.add('is-complete');
            if (!container.dataset.completed) {
              container.dataset.completed = 'true';
            }
            
            // Mark root element as complete to adjust z-index/positioning
            root.classList.add('is-complete');
          }
        } else if (bulletsContainer) {
          const container = /** @type {HTMLElement} */ (bulletsContainer);
          container.classList.remove('is-complete');
          container.dataset.completed = '';
          
          // Remove complete class from root
          root.classList.remove('is-complete');
        }
        
        // Calculate how many previews we can show based on real container space.
        // Goal: use *available space* instead of capping at 1–2 previews.
        const usableSpace = getBulletsUsableHeight();
        const availableSpace = typeof usableSpace === 'number' ? usableSpace : space.availableSpace;
        const padding = 12; // safety buffer so previews don't collide with container edges
        const spacing = 8; // must match translateY spacing
        const maxPreviews = 6; // upper bound to avoid overly long stacks

        const endFillMode = allShown && effectiveActive === lastBulletIndex;

        /**
         * Reset a bullet element to a clean slate.
         * @param {HTMLElement} el
         */
        const resetBullet = (el) => {
          el.classList.remove('is-active', 'is-visible', 'is-adjacent', 'is-above', 'is-past', 'is-far-past', 'is-preview');
          el.style.display = '';
          el.style.opacity = '';
          el.style.pointerEvents = '';
          el.style.filter = '';
          el.style.transform = '';
        };

        /**
         * Apply preview styles consistently.
         * @param {HTMLElement} el
         * @param {number} translateY
         * @param {number} scale
         * @param {number} level - 1-based preview level
         */
        const applyPreview = (el, translateY, scale, level) => {
          if (level === 1) {
            el.classList.add('is-adjacent');
            el.style.opacity = '';
            el.style.filter = '';
          } else {
            el.classList.add('is-preview');
            // Slightly fade deeper previews; keep CSS defaults if level is small.
            const o = Math.max(0.06, 0.22 - (level - 2) * 0.04);
            el.style.opacity = String(o);
            el.style.filter = level >= 4 ? 'blur(1px)' : '';
          }
          el.style.pointerEvents = 'none';
          el.style.transform = `translateX(0) translateY(${translateY}px) scale(${scale})`;
        };

        /**
         * Build a preview plan for indices stacked below a top bullet.
         * Returns a map of index -> {y, scale, level}.
         * @param {number[]} indices - ordered from closest to furthest.
         * @param {number} baseY
         * @param {number} firstScale
         * @param {number} restScale
         * @returns {Record<number, { y: number, scale: number, level: number }>}
         */
        const buildPreviewPlan = (indices, baseY, firstScale, restScale) => {
          /** @type {Record<number, { y: number, scale: number, level: number }>} */
          const plan = {};
          let y = baseY;
          let level = 1;

          for (let i = 0; i < indices.length; i += 1) {
            if (level > maxPreviews) break;
            const idx = indices[i];
            if (typeof idx !== 'number') continue;
            const el = bullets[idx];
            if (!el) continue;

            const scale = level === 1 ? firstScale : restScale;
            const h = el.offsetHeight || 0;
            const bottom = y + (h * scale) + padding;
            if (bottom > availableSpace) break;

            plan[idx] = { y, scale, level };
            y += (h * scale) + spacing;
            level += 1;
          }

          return plan;
        };

        // Precompute preview plans for current state.
        const previewPlan = (() => {
          // Before first bullet activates: bullet[0] is shown as adjacent at y=0 (scale 0.88).
          if (effectiveActive === -1) {
            const first = bullets[0];
            const firstH = first?.offsetHeight || 80;
            const future = [];
            for (let i = 1; i < bullets.length; i += 1) future.push(i);
            // Keep legacy translate behavior: start stacking after full first bullet height + spacing.
            // Use scale 0.84 for all previews in this state (matches existing code).
            return buildPreviewPlan(future, firstH + spacing, 0.84, 0.84);
          }

          // Normal: show as many FUTURE bullets as can fit below active, unless we're in end-fill mode.
          if (!endFillMode) {
            const future = [];
            for (let i = effectiveActive + 1; i < bullets.length; i += 1) future.push(i);
            const activeEl = bullets[effectiveActive];
            const activeH = activeEl?.offsetHeight || 80;
            return buildPreviewPlan(future, activeH + spacing, 0.88, 0.84);
          }

          // End-fill mode: last bullet is active; stack PREVIOUS bullets below it to avoid empty space.
          const past = [];
          for (let i = effectiveActive - 1; i >= 0; i -= 1) past.push(i);
          const activeEl = bullets[effectiveActive];
          const activeH = activeEl?.offsetHeight || 80;
          return buildPreviewPlan(past, activeH + spacing, 0.88, 0.84);
        })();
        
        bullets.forEach((el, index) => {
          // Handle case where no bullet is active yet (effectiveActive = -1)
          if (effectiveActive === -1) {
            resetBullet(el);

            if (index === 0) {
              // Always show first bullet as adjacent for a smooth transition into active state.
              el.classList.add('is-adjacent');
              el.style.opacity = '';
              el.style.pointerEvents = 'auto';
              el.style.transform = 'translateX(0) translateY(0) scale(0.88)';
            } else if (previewPlan[index]) {
              const p = previewPlan[index];
              applyPreview(el, p.y, p.scale, p.level + 1); // +1 because bullet[0] occupies the first visual slot
            } else {
              el.style.opacity = '0';
              el.style.pointerEvents = 'none';
            }
            return;
          }
          
          const isActive = index === effectiveActive;
          const isPrevious = index < effectiveActive;
          const isFarPast = index < effectiveActive - 1; // Bullets that are 1+ positions behind

          resetBullet(el);

          if (isActive) {
            // Active bullet: fully visible on top
            // Ensure bullet is visible before adding active class for proper transition animation
            // This is especially important for first bullet activating early
            if (el.style.opacity === '0' || el.style.opacity === '') {
              // Bullet was hidden, make it visible first to enable transition
              el.style.opacity = '1';
              el.style.pointerEvents = 'auto';
            }
            // Add active class - CSS transitions will handle the animation
            el.classList.add('is-active');
            // Reset transform to ensure active bullet is at top
            el.style.transform = 'translateX(0) translateY(0) scale(1)';
          } else if (previewPlan[index]) {
            const p = previewPlan[index];
            applyPreview(el, p.y, p.scale, p.level);
          } else if (isPrevious) {
            if (!endFillMode) {
              // Previous bullets: stacked behind, faded (normal mode)
              el.classList.add('is-past');
              if (isFarPast) {
                el.classList.add('is-far-past');
              }
              el.style.pointerEvents = 'none';
              // Reset transform to use CSS defaults for past bullets
              el.style.transform = '';
            } else {
              // In end-fill mode, hide previous bullets unless included in previewPlan.
              el.style.opacity = '0';
              el.style.pointerEvents = 'none';
            }
          } else {
            // Future bullets: hidden
            el.style.opacity = '0';
            el.style.pointerEvents = 'none';
            el.style.transform = '';
          }
        });
      } else {
        // Desktop: Show all bullets, just toggle active state
        // First bullet is not active by default (active = -1 until its cue time is reached)
        const effectiveActive = active >= 0 ? active : -1;
        bullets.forEach((el, index) => {
          // Only mark as active if effectiveActive is valid (>= 0) and matches this index
          if (effectiveActive >= 0 && index === effectiveActive) {
            el.classList.add('is-active');
          } else {
            el.classList.remove('is-active');
          }
          el.classList.remove('is-visible', 'is-adjacent', 'is-above');
        });
      }
    };

    const step = () => {
      // Check for viewport changes before processing
      const viewportChanged = checkViewportChange();
      if (viewportChanged && latestFrame >= 0) {
        // Viewport changed - redraw current frame with new config
        const currentFrame = latestFrame;
        latestFrame = -1; // Force redraw
        drawFrame(currentFrame);
      }
      
      const scrollY = window.scrollY || window.pageYOffset || 0;
      
      // Validate that end > start to prevent division by zero or invalid progress
      if (end <= start) {
        measure();
        // If still invalid after remeasure, skip this frame
        if (end <= start) {
          window.requestAnimationFrame(step);
          return;
        }
      }
      
      // Detect scroll direction early (needed for flag clearing)
      const scrollDelta = scrollY - lastScrollY;
      const isScrollingUp = scrollDelta < -1;
      
      // If we were past the animation end and are now scrolling back into the range,
      // remeasure immediately. This prevents stale start/end causing progress to snap to 0
      // (showing the first frame) when re-entering from below.
      // Track that we're coming from below to maintain last callout state
      if (lastScrollY > end && scrollY <= end) {
        measure();
        // Allow reverse to progress back through callouts instead of staying locked on last
        // But preserve that we came from below so we start at last callout
        hasCompletedAnimation = false;
        isComingFromBelow = true; // Set persistent flag
      }
      
      // Clear the flag when we're clearly past the transition or scrolling forward from above
      if (scrollY < start - 50 || (scrollY > end + 50 && !isScrollingUp)) {
        isComingFromBelow = false;
      }

      // Check if section is in viewport and in focus before updating
      const rect = root.getBoundingClientRect();
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 1;
      const headerHeight = parseInt(getComputedStyle(document.body).getPropertyValue('--header-height')) || 60;
      const stickyTopTarget = (() => {
        // The sticky "pin" element in this section is `root` itself.
        // It is positioned with CSS `top: <value>`. Desktop is `top: 0`.
        const topValue = parseFloat(getComputedStyle(root).top);
        return Number.isFinite(topValue) ? topValue : 0;
      })();
      
      // Calculate progress FIRST to check completion state
      let raw = (scrollY - start) / (end - start);
      
      // Store original progress for first bullet (not delayed)
      let rawOriginal = raw;
      let progressOriginal = clamp01(rawOriginal);
      
      // Apply mobile delay - shift animation start later on mobile (for frameIndex only)
      const isMobile = window.innerWidth <= 749;
      const mobileDelay = isMobile ? 0.12 : 0; // 12% delay on mobile (callouts start later)
      if (isMobile && raw > 0) {
        // Adjust raw progress to account for delay
        // When raw = mobileDelay, progress should be 0
        // When raw = 1, progress should be 1
        raw = Math.max(0, (raw - mobileDelay) / (1 - mobileDelay));
      }
      
      let progress = clamp01(raw);
      const isInAnimationRange = progress > 0 && progress < 1;
      
      // Check if we're within the proper scroll range (where animation actually happens)
      const isInProperRange = scrollY >= start && scrollY <= end;
      
      // When coming from below and just entered the section, maintain progress = 1.0
      // until we're actually in the animation range to prevent jumping to first callout
      // Only override if progress is low (near start) to maintain last callout
      if (isComingFromBelow && isInProperRange && progress < 0.5 && isScrollingUp) {
        // Override progress to 1.0 to maintain last callout when first entering from below
        progress = 1.0;
        raw = 1.0;
        // Also override progressOriginal for bullet updates
        progressOriginal = 1.0;
        rawOriginal = 1.0;
      }
      
      // Clear the flag once we're actually in the animation range and scrolling back
      if (isComingFromBelow && isInAnimationRange && isScrollingUp) {
        isComingFromBelow = false; // Let normal reverse animation take over
      }
      
      // Track completion state - once animation completes, mark it
      // Don't reset when reversing - we want to keep last bullet active
      // But allow frame animation to reverse by not blocking updates
      if (progress >= 1.0) {
        hasCompletedAnimation = true;
      } else if (scrollY < start - 50) {
        // Reset completion state when we're clearly before the section
        hasCompletedAnimation = false;
      }
      
      // Check if sticky element is actually pinned (this means section is "full screen")
      // Compare against the element's configured sticky top (usually 0), not header height.
      // Header height was incorrectly used before and prevented reverse from ever enabling.
      const isStickyPinned = Math.abs(rect.top - stickyTopTarget) <= 4;
      
      // Check if section is visible in viewport
      const isVisible = rect.bottom > headerHeight && rect.top < viewportHeight;
      
      // Check if sticky add-to-cart is hidden (closed)
      // The sticky add-to-cart hides itself when the section is actively animating
      // This is a more reliable indicator than calculating section visibility
      const stickyAddToCart = document.querySelector('.sticky-add-to-cart');
      const isStickyAddToCartHidden = !!(stickyAddToCart && stickyAddToCart.getAttribute('aria-hidden') === 'true');
      
      // If we've scrolled past the section, keep last bullet active and don't update canvas
      // This prevents jumping to wrong frames when scrolling past
      if (scrollY > end) {
        // Always mark as completed when past section to ensure proper reverse behavior
        if (!hasCompletedAnimation) {
          hasCompletedAnimation = true;
        }
        // Keep last bullet active, show last frame
        updateBullets(1.0, cfg.frames - 1, false);
        drawFrame(cfg.frames - 1);
        window.requestAnimationFrame(step);
        return;
      }
      
      // Before reaching the section, show first frame (frame 0) and clear completion UI
      // This runs even if the section is not currently visible so we don't keep the
      // last callout stuck when re-entering from above.
      if (scrollY < start) {
        hasCompletedAnimation = false;
        const bulletsContainer = /** @type {HTMLElement|null} */ (root.querySelector('[data-sequence-bullets]'));
        if (bulletsContainer) {
          bulletsContainer.classList.remove('is-complete');
          bulletsContainer.dataset.completed = '';
        }
        drawFrame(0);
        // Check if sticky add-to-cart is hidden even before animation starts
        const stickyAddToCartBefore = document.querySelector('.sticky-add-to-cart');
        const isStickyAddToCartHiddenBefore = !!(stickyAddToCartBefore && stickyAddToCartBefore.getAttribute('aria-hidden') === 'true');
        updateBullets(0, 0, isStickyAddToCartHiddenBefore);
        window.requestAnimationFrame(step);
        return;
      }
      
      // Only allow updates when section is visible and in proper range
      if (!isVisible) {
        window.requestAnimationFrame(step);
        return;
      }
      
      // Wait until section is pinned before allowing animation
      // This prevents callouts from moving before the screen is locked
      // Check if we just entered the section (from above or below) and aren't pinned yet
      const wasBeforeSection = lastScrollY < start;
      const wasPastSection = lastScrollY > end;
      const justEnteredSection = (wasBeforeSection || wasPastSection) && isInProperRange;
      
      if (justEnteredSection && !isStickyPinned) {
        // Just entered the section but not pinned yet - wait until pinned
        // Keep appropriate state based on where we came from
        if (wasPastSection) {
          // Coming from below - ALWAYS keep last frame/bullet (regardless of hasCompletedAnimation)
          // This ensures smooth reverse animation when scrolling back
          if (!hasCompletedAnimation) {
            hasCompletedAnimation = true; // Mark as completed so reverse works properly
          }
          updateBullets(1.0, cfg.frames - 1, false);
          drawFrame(cfg.frames - 1);
        } else if (wasBeforeSection) {
          // Coming from above - keep first frame/bullet
          const stickyAddToCartBefore = document.querySelector('.sticky-add-to-cart');
          const isStickyAddToCartHiddenBefore = !!(stickyAddToCartBefore && stickyAddToCartBefore.getAttribute('aria-hidden') === 'true');
          updateBullets(0, 0, isStickyAddToCartHiddenBefore);
          drawFrame(0);
        }
        window.requestAnimationFrame(step);
        return;
      }
      
      // For reverse scrolling, handle cases when not in proper range
      if (isScrollingUp) {
        // If not in proper range, keep the completed state but don't advance
        if (!isInProperRange) {
          if (hasCompletedAnimation) {
            updateBullets(1.0, cfg.frames - 1, false);
            drawFrame(cfg.frames - 1);
          }
          window.requestAnimationFrame(step);
          return;
        }
      }
      
      // Only update when in proper range
      if (!isInProperRange) {
        window.requestAnimationFrame(step);
        return;
      }
      
      // CRITICAL: Wait until section is pinned before allowing ANY animation
      // This ensures both canvas and callouts only animate when screen is locked
      if (!isStickyPinned) {
        // Not pinned yet - keep current state and wait
        if (isComingFromBelow || hasCompletedAnimation) {
          // Coming from below or completed - keep last frame/bullet
          updateBullets(1.0, cfg.frames - 1, false);
          drawFrame(cfg.frames - 1);
        } else {
          // Coming from above - keep first frame/bullet
          const stickyAddToCartWait = document.querySelector('.sticky-add-to-cart');
          const isStickyAddToCartHiddenWait = !!(stickyAddToCartWait && stickyAddToCartWait.getAttribute('aria-hidden') === 'true');
          updateBullets(0, 0, isStickyAddToCartHiddenWait);
          drawFrame(0);
        }
        window.requestAnimationFrame(step);
        return;
      }
      
      // Now we're pinned - allow animation to proceed
      
      // When scrolling back up from below, ensure we remeasure to get correct start/end values
      // But only if we're approaching the section (not when way past it)
      // Only remeasure when we're NOT in the active animation range to avoid resets mid-scroll.
      if (!isInAnimationRange) {
        if ((scrollY > end + 200 && scrollY < end + 500) || (scrollY < start - 200 && scrollY > start - 500)) {
          measure();
        }
      }
      
      // Periodically check if remeasurement is needed (every ~2 seconds)
      // Only remeasure when we're NOT actively in the animation range
      remeasureCheckCounter++;
      if (remeasureCheckCounter >= REMEASURE_CHECK_INTERVAL) {
        remeasureCheckCounter = 0;
        
        // Only remeasure if we're clearly outside the animation range AND not currently in range.
        // This avoids mid-animation resets, while still allowing re-entry from below to recalc start/end.
        if (!isInAnimationRange && !isInProperRange) {
          const rect = root.getBoundingClientRect();
          const expectedStart = scrollY + rect.top;
          
          // Only remeasure if values are significantly off (larger threshold)
          // This prevents unnecessary remeasurements during normal scrolling
          if (Math.abs(expectedStart - start) > 100 || Math.abs((scrollY + rect.top + (end - start)) - end) > 100) {
            measure();
          }
        }
        
        // Update lastScrollY for next check
        lastScrollY = scrollY;
      }
      
      // Ensure animation completes (progress = 1.0, frame 240) and all bullets shown
      // Progress maps to: frame 0-240, time 0-9.6s
      // Last bullet is at 7.2s (progress = 0.75), animation completes at 9.6s (progress = 1.0)
      const frameIndex = Math.round(progress * (cfg.frames - 1));
      // const currentTime = progress * cfg.duration; // unused, kept here if you want to debug timing

      // Preload frames around current frame (only if optimizations enabled)
      if (ENABLE_OPTIMIZATIONS) {
        preloadFrames(frameIndex);
      }
      
        drawFrame(frameIndex);
        // Pass original progress so first bullet isn't affected by mobileDelay
        // Other bullets will apply the delay in updateBullets
        updateBullets(progressOriginal, frameIndex, isStickyAddToCartHidden);
      
      // Update lastScrollY for scroll direction detection in next frame
      lastScrollY = scrollY;

      window.requestAnimationFrame(step);
    };

    const drawPoster = () => {
      if (!poster || !poster.complete) return;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(poster, 0, 0, canvas.width, canvas.height);
    };

    if (poster) {
      if (poster.complete) drawPoster();
      else poster.onload = drawPoster;
    }

    if (prefersReduce && cfg.honorReduceMotion) {
      drawFrame(0);
      // Check if section is visible for reduced motion mode
      const rect = root.getBoundingClientRect();
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 1;
      const headerHeight = parseInt(getComputedStyle(document.body).getPropertyValue('--header-height')) || 60;
      const isVisible = rect.bottom > headerHeight && rect.top < viewportHeight;
      const stickyAddToCartReduced = document.querySelector('.sticky-add-to-cart');
      const isStickyAddToCartHiddenReduced = !!(stickyAddToCartReduced && stickyAddToCartReduced.getAttribute('aria-hidden') === 'true');
      updateBullets(0, 0, isStickyAddToCartHiddenReduced); // progress = 0, frameIndex = 0
      // Clean up observer if it was created
      if (intersectionObserver) {
        intersectionObserver.disconnect();
      }
      return;
    }

    // Ensure initial measurement is correct, especially if section is already in view
    const checkInitialPosition = () => {
      const rect = root.getBoundingClientRect();
      // If section is already visible, remeasure after a short delay to ensure layout is settled
      if (rect.top < window.innerHeight * 2 && rect.bottom > -window.innerHeight) {
        setTimeout(measure, 100);
      }
    };
    
    // Check initial position after a brief delay to allow layout to settle
    setTimeout(checkInitialPosition, 100);

    window.requestAnimationFrame(step);
  };

  const bootstrap = () => {
    const roots = document.querySelectorAll('[data-sequence-root]');
    roots.forEach((root) => initSequence(/** @type {HTMLElement} */ (root)));
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap);
  } else {
    bootstrap();
  }
})();
